import React from 'react';
import { Link } from 'react-router-dom';
import { Award, TrendingUp, Users, Star, ChevronRight, Trophy, Target, Briefcase } from 'lucide-react';

const COVER_PHOTO = 'https://customer-assets.emergentagent.com/job_110eaf2c-e2da-4799-9c05-6d381eae4282/artifacts/dxl4hfqk_26cover.png';
const AFCA_IMG = 'https://customer-assets.emergentagent.com/job_669fbeff-f080-4dde-bc05-7b6a5c8193e8/artifacts/e6nzyx3n_Braxton__Weldon_FB_png_0PLnr6iK.jpeg';

const HEADLINE_STATS = [
  { value: 'Head Coach', label: 'College Level' },
  { value: 'Championship', label: 'Program Builder' },
  { value: '#1 Offense', label: 'MCAC Conference' },
  { value: '#2 National', label: 'NJCAA Ranking' },
];

const CAREER_HIGHLIGHTS = [
  { icon: Trophy, title: 'Conference Championship', desc: 'Built a non-scholarship program into MCAC conference champions — the first in school history.' },
  { icon: TrendingUp, title: '#1 Ranked Offense', desc: 'Led the top-ranked offense in the MCAC and a top-10 nationally ranked NJCAA offense.' },
  { icon: Star, title: 'First Bowl Game in School History', desc: 'Transformed program from the ground up and led the team to its first-ever bowl appearance.' },
  { icon: Award, title: 'MCAC Coach of the Year', desc: 'Recognized as the top coach in the conference after a championship-winning season.' },
  { icon: Target, title: 'School Records Set', desc: 'Set school records in total offense and points per game across multiple seasons.' },
  { icon: Users, title: '8 JUCO All-Americans', desc: 'Developed and coached 8 JUCO All-Americans, helping athletes advance to the next level.' },
];

const EXPERIENCE_TIMELINE = [
  { role: 'Head Coach / Offensive Coordinator', org: 'Itasca Community College', years: '2017 – 2020', highlights: ['Conference Championship', '#1 MCAC Offense (2018, 2019)', '#2 NJCAA National Ranking', 'MCAC Coach of the Year', 'First Bowl Game in School History', '8 JUCO All-Americans'] },
  { role: 'Offensive Coordinator / Recruiting', org: 'Sussex County Community College', years: '2025', highlights: ['Program development', 'Offensive system installation'] },
  { role: 'Offensive Coordinator', org: 'Fivay High School', years: '2022 – 2023', highlights: ['Offensive system design', 'Player development'] },
  { role: 'Graduate Assistant / Offensive Staff', org: 'Arkansas Tech University', years: '2013 – 2016', highlights: ['D2 football operations', 'Founded Golden Eagles flag football program'] },
];

const CREDENTIALS = [
  'College Head Coach',
  'Conference Championship Builder',
  'Offensive Coordinator',
  'AFCA 35 Under 35 (Selected 2021, Inducted 2022)',
  'MCAC Coach of the Year',
  'M.S. Sport Administration — Liberty University',
  'B.S. Recreation & Park Administration — Arkansas Tech University',
];

export default function HomePage() {
  return (
    <div className="bg-gray-950 text-white">
      {/* Hero */}
      <section className="pt-20">
        <div className="container mx-auto max-w-7xl px-6 pt-8">
          <img src={COVER_PHOTO} alt="Weldon Braxton" className="w-full h-auto rounded-2xl shadow-2xl" />
        </div>

        <div className="container mx-auto max-w-5xl px-6 pt-12 pb-8 text-center">
          <h1 data-testid="hero-heading" className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 leading-tight tracking-tight">
            Weldon Braxton
          </h1>
          <p className="text-xl md:text-2xl text-blue-400 font-semibold mb-6">
            College Football Head Coach &middot; Offensive Coordinator &middot; Program Builder
          </p>
          <p className="text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            Championship program builder with a proven track record of developing elite offenses, winning conference titles, and advancing players to the next level.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/experience" className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-4 rounded-full text-lg transition-all hover:scale-105 shadow-lg shadow-blue-600/20 inline-flex items-center justify-center">
              View Full Experience <ChevronRight className="ml-2 h-5 w-5" />
            </Link>
            <Link to="/contact" className="border-2 border-gray-600 hover:border-gray-400 text-white font-semibold px-8 py-4 rounded-full text-lg transition-all inline-flex items-center justify-center">
              Get in Touch
            </Link>
          </div>
        </div>

        {/* Stat Bar */}
        <div className="border-y border-gray-800 bg-gray-900/60">
          <div className="container mx-auto max-w-5xl px-6 py-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {HEADLINE_STATS.map((s) => (
                <div key={s.label}>
                  <p className="text-2xl md:text-3xl font-black text-white">{s.value}</p>
                  <p className="text-gray-500 text-sm mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Career Highlights */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold mb-3">Career Highlights</h2>
            <p className="text-gray-500">Results built through coaching, leadership, and relentless program development.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {CAREER_HIGHLIGHTS.map((h) => (
              <div key={h.title} className="bg-gray-900 border border-gray-800 rounded-2xl p-7 hover:border-blue-600/40 transition-all group">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-blue-600/10 border border-blue-600/20 mb-5 group-hover:bg-blue-600/20 transition-colors">
                  <h.icon className="h-6 w-6 text-blue-500" />
                </div>
                <h3 className="text-lg font-bold mb-2">{h.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{h.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AFCA Feature */}
      <section className="py-20 px-6 bg-gray-900 border-y border-gray-800">
        <div className="container mx-auto max-w-5xl">
          <div className="bg-gradient-to-r from-blue-950/60 to-gray-900 border border-blue-800/30 rounded-2xl overflow-hidden">
            <div className="grid md:grid-cols-2 gap-8 items-center p-8 md:p-12">
              <div>
                <span className="inline-block px-3 py-1 bg-blue-600/20 text-blue-400 text-xs font-bold rounded-full mb-4">National Recognition</span>
                <h2 className="text-3xl font-bold mb-4">AFCA 35 Under 35 Leadership Institute</h2>
                <p className="text-gray-400 mb-2">Selected 2021 &middot; Inducted 2022</p>
                <p className="text-gray-400 text-sm leading-relaxed">Recognized nationally by the American Football Coaches Association as one of the top 35 football coaches under 35 for leadership, impact, and contribution to the profession.</p>
              </div>
              <img src={AFCA_IMG} alt="AFCA 35 Under 35" className="rounded-xl w-full shadow-lg" />
            </div>
          </div>
        </div>
      </section>

      {/* Experience Timeline */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-14">Coaching Experience</h2>
          <div className="space-y-6">
            {EXPERIENCE_TIMELINE.map((exp) => (
              <div key={exp.org} className="bg-gray-900 border border-gray-800 rounded-2xl p-8 hover:border-blue-600/30 transition-colors">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold">{exp.role}</h3>
                    <p className="text-blue-400 font-semibold">{exp.org}</p>
                  </div>
                  <span className="text-gray-500 text-sm font-medium mt-1 md:mt-0 md:bg-gray-800 md:px-4 md:py-1.5 md:rounded-full">{exp.years}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {exp.highlights.map((h) => (
                    <span key={h} className="bg-gray-800 border border-gray-700 text-gray-300 text-xs px-3 py-1.5 rounded-full">{h}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/experience" className="text-blue-400 hover:text-blue-300 font-semibold transition-colors">View Full Experience &rarr;</Link>
          </div>
        </div>
      </section>

      {/* Credentials */}
      <section className="py-20 px-6 bg-gray-900 border-y border-gray-800">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-14">Credentials &amp; Education</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {CREDENTIALS.map((c) => (
              <div key={c} className="flex items-center space-x-3 bg-gray-950 border border-gray-800 rounded-xl px-5 py-4">
                <Briefcase className="h-5 w-5 text-blue-500 flex-shrink-0" />
                <span className="text-gray-300 text-sm font-medium">{c}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Let&apos;s Connect</h2>
          <p className="text-gray-400 leading-relaxed mb-10">
            Interested in working together, discussing coaching, or connecting on opportunities? Reach out directly.
          </p>
          <Link to="/contact" className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-4 rounded-full transition-all inline-flex items-center">
            Get in Touch <ChevronRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
